import type { RouteLocationNormalized } from "vue-router"

/**
 * Routes fiche bâtiment (sidebar building ou URL `/building/:id/...`).
 */
export function isBuildingSidebarRoute(
    route: RouteLocationNormalized
): boolean {
    if (route.meta.sidebar === "building") return true
    return /^\/building\/[^/]+/.test(route.path)
}

/**
 * Routes fiche source (sidebar source ou URL `/source/:id/...`), hors liste `/source`.
 */
export function isSourceSidebarRoute(route: RouteLocationNormalized): boolean {
    if (route.meta.sidebar === "source") return true
    return /^\/source\/[^/]+/.test(route.path)
}

/**
 * Statuts HTTP qui constituent un VRAI refus d'accès.
 *
 * Tout le reste — 5xx, panne réseau, timeout — est une défaillance de notre côté.
 * `handleServiceCall` donne 500 aux erreurs sans réponse HTTP, donc le réseau tombe
 * bien dans « technique ».
 */
const STATUTS_REFUS_ACCES = new Set([401, 403, 404])

/**
 * L'échec de chargement est-il un refus d'accès, ou une panne technique ?
 *
 * Sans cette distinction, un 500 était annoncé « Accès refusé — vous n'avez pas accès
 * à cette source » : le message accusait l'utilisateur d'un problème de droits qu'il
 * n'avait pas. Le 26/08/2026, un client a repris ce libellé pour décrire sa panne ;
 * le support a détaché puis rattaché sa source, et le triage automatique a bâti son
 * analyse sur les droits — deux jours avant qu'on regarde le vrai 500 (DEV-1515,
 * cause dans DEV-1509).
 *
 * @param error - Erreur capturée (typiquement une `ServiceError`)
 * @returns `true` si l'utilisateur n'a effectivement pas accès à la ressource
 */
export function estRefusDacces(error: unknown): boolean {
    const status = (error as { status?: number | null } | null)?.status
    return typeof status === "number" && STATUTS_REFUS_ACCES.has(status)
}

/** Textes toast affichés après redirection pour accès refusé (FR). */
export const restrictedAccessRedirectToast = {
    building: {
        summary: "Accès refusé",
        detail: "Vous n'avez pas accès à ce bâtiment.",
    },
    source: {
        summary: "Accès refusé",
        detail: "Vous n'avez pas accès à cette source (objet connecté).",
    },
    /** Après redirection : lien CompanySource `views` révoqué (GET source métier). */
    sourceViewsLinkRevoked: {
        summary: "Source plus accessible",
        detail: "Cette source a été retirée : vous n'y avez plus accès.",
    },
} as const

/**
 * Textes toast affichés après redirection pour une PANNE TECHNIQUE (5xx, réseau).
 *
 * Ils disent trois choses que « Accès refusé » ne disait pas : que le problème vient
 * de nous, que l'utilisateur n'y est pour rien, et qu'il peut réessayer.
 */
export const fetchErrorRedirectToast = {
    building: {
        summary: "Chargement impossible",
        detail: "Une erreur technique de notre côté nous a empêchés de charger ce bâtiment. Réessayez dans quelques instants.",
    },
    source: {
        summary: "Chargement impossible",
        detail: "Une erreur technique de notre côté nous a empêchés de charger cette source (objet connecté). Réessayez dans quelques instants.",
    },
} as const
